/**
 * Cliente del wizard de baja ("Quiero dejar de ser parte de Psi Mammoliti"),
 * migrado a psiplatform-be (antes era un proyecto Lovable aparte, embebido por
 * iframe — ver FarewellEmbed.tsx). Todo el estado vive en el backend, atado a
 * la sesión real de la psicóloga (sin login ni link compartible por email).
 */

import { beHttp } from './beHttp';

export type Decision = 'derivar' | 'acompanar' | 'alta';
export type EstadoProceso = 'En curso' | 'Retención' | 'Próximo a alta';

export interface Paso1State {
  reason: string;
  reasonOther?: string;
  trigger?: string;
  wouldChange?: string;
  recommend: number;
}

export interface PlanPaciente {
  decision: Decision;
  justificacion?: string;
}

/** Snapshot de nombre por paciente al momento del paso 2 — le permite al
 * panel de admin (psima) mostrar el nombre real en vez del id, incluso si el
 * proceso cambia de estado después. Campo snake_case a propósito: es el
 * shape que `psima` ya espera en `paso2.pacientes_snapshot`. */
export interface PacienteSnapshot {
  id: string;
  nombre: string;
}

export interface Paso2State {
  planes: Record<string, PlanPaciente>;
  disclosureOpen?: boolean;
  disclosureText?: string;
  pacientes_snapshot?: PacienteSnapshot[];
}

export const HISTORIA_CLINICA_FIELDS = [
  'motivoConsulta',
  'evaluacionDiagnostica',
  'antecedentes',
  'evolucion',
  'estadoActual',
  'objetivos',
  'evaluacionRiesgos',
  'observaciones',
  'notaTraspaso',
] as const;
export type HistoriaClinicaField = (typeof HISTORIA_CLINICA_FIELDS)[number];

export type HistoriaClinica = Record<HistoriaClinicaField, string>;

export type HcFieldStatus = 'approved' | 'warning' | 'rejected';
export type HcOverallStatus = 'approved' | 'needs_revision' | 'rejected';

export interface HcFieldEvaluation {
  fieldId: HistoriaClinicaField;
  status: HcFieldStatus;
  message: string;
}

export interface HcValidationResult {
  overallStatus: HcOverallStatus;
  approvedCount: number;
  fields: HcFieldEvaluation[];
  disclaimer: string;
}

export interface PlanTransicion {
  decision: Decision;
  fechaUltimaSesion?: string | null;
  fechaAlta?: string | null;
  fechaCierre?: string | null;
  aspectos?: string;
  preferenciaPerfil?: string;
  momento?: string;
  justificacion?: string;
  acuerdo?: string;
  historiaClinica: HistoriaClinica;
  validacionIA?: HcValidationResult | null;
  confirmado?: boolean;
}

export type Paso3State = Record<string, PlanTransicion>;

export interface Paso4State {
  confirmado: boolean;
}

/** @deprecated Legacy — flujo de horarios propuestos, reemplazado por el
 * checkbox `reunionCoordinada`. Solo aparece al leer bajas completadas antes
 * del cambio (ver `assemblePaso5` en el backend); el wizard nuevo nunca lo
 * envía. */
export interface ReunionSlot {
  fechaHoraIso: string;
}

export interface Paso5State {
  /** true si la psicóloga declaró que ya coordinó o va a coordinar la
   * reunión de cierre directo con administracion@psimammoliti.com. */
  reunionCoordinada: boolean;
  notas?: string;
  /** @deprecated Legacy — ver `ReunionSlot`. */
  slots?: ReunionSlot[];
}

export interface BajaProgress {
  fechaSalidaIso: string | null;
  pasosCompletos: number[];
  paso1: Paso1State | null;
  paso2: Paso2State | null;
  paso3: Paso3State | null;
  paso4: Paso4State | null;
  paso5: Paso5State | null;
  completedAt: string | null;
}

export interface BajaPaciente {
  id: string;
  nombre: string;
  antiguedad: string;
  frecuencia: string;
  ultimaSesion: string;
  proximaSesion: string;
  estado: EstadoProceso;
  sesionesRealizadas: number;
}

export interface SaveBajaProgressInput {
  fechaSalidaIso?: string;
  pasosCompletos?: number[];
  paso1?: Paso1State;
  paso2?: Paso2State;
  paso3?: Paso3State;
  paso4?: Paso4State;
  paso5?: Paso5State;
}

export const bajaApi = {
  getProgress(): Promise<BajaProgress> {
    return beHttp.get<BajaProgress>('/api/psychologist/baja/progress');
  },
  saveProgress(input: SaveBajaProgressInput): Promise<BajaProgress> {
    return beHttp.patch<BajaProgress, SaveBajaProgressInput>(
      '/api/psychologist/baja/progress',
      input,
      { showErrorToast: false },
    );
  },
  getPatients(): Promise<{ results: BajaPaciente[] }> {
    return beHttp.get<{ results: BajaPaciente[] }>(
      '/api/psychologist/baja/patients',
    );
  },
  validateHistoriaClinica(hc: HistoriaClinica): Promise<HcValidationResult> {
    return beHttp.post<HcValidationResult, { hc: HistoriaClinica }>(
      '/api/psychologist/baja/validate-hc',
      { hc },
      { showErrorToast: false },
    );
  },
  complete(paso5: Paso5State): Promise<{ sent: boolean }> {
    return beHttp.post<{ sent: boolean }, { paso5: Paso5State }>(
      '/api/psychologist/baja/complete',
      { paso5 },
    );
  },
};
